import React, { useState, useEffect } from 'react';
import { useAuth } from '@/App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import {
  Mail, Plus, Calendar, Clock, Send, Trash2, Edit2, Play, Eye,
  CheckCircle2, XCircle, Loader2, RefreshCw, Settings2, Users,
  FileText, Download, History, Bell, Sparkles, AlertTriangle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FREQUENCY_OPTIONS = [
  { value: 'daily', label: 'Daily', description: 'Every day at 7:00 AM', icon: '📅' },
  { value: 'weekly', label: 'Weekly', description: 'Every Monday at 7:00 AM', icon: '📆' },
  { value: 'monthly', label: 'Monthly', description: '1st of each month at 7:00 AM', icon: '🗓️' }
];

const DOMAIN_OPTIONS = [
  { value: 'all', label: 'All Domains', description: 'Comprehensive report', color: 'bg-[#00b0ab]/100' },
  { value: 'clinical', label: 'Clinical Quality', description: 'Pain control, hospitalization', color: 'bg-emerald-500' },
  { value: 'financial', label: 'Financial Health', description: 'Revenue, margins', color: 'bg-amber-500' },
  { value: 'workforce', label: 'Workforce', description: 'Staff, burnout risk', color: 'bg-blue-500' },
  { value: 'compliance', label: 'Compliance', description: 'CTI, F2F, CHAP', color: 'bg-teal-500' },
  { value: 'census', label: 'Census', description: 'ADC, admissions', color: 'bg-purple-500' }
];

const REPORT_TYPES = [
  { value: 'summary', label: 'Summary Only', description: 'Email with key metrics summary' },
  { value: 'full', label: 'Full Report + PDF', description: 'Email summary with PDF attachment' }
];

export function ScheduledReportManager({ className }) {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [schedules, setSchedules] = useState([]);
  const [deliveryLogs, setDeliveryLogs] = useState([]);
  const [activeTab, setActiveTab] = useState('schedules');
  
  // Dialog states
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [previewHtml, setPreviewHtml] = useState('');
  
  // Form states
  const [formData, setFormData] = useState({
    name: '',
    frequency: 'weekly',
    report_type: 'summary',
    domains: ['all'],
    recipients: '',
    is_active: true
  });
  const [submitting, setSubmitting] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [sendingTest, setSendingTest] = useState(false);

  const fetchSchedules = async () => {
    try {
      const res = await fetch(`${API_URL}/api/scheduled-reports/schedules`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSchedules(data.schedules || []);
      }
    } catch (error) {
      console.error('Failed to fetch schedules:', error);
    }
  };

  const fetchDeliveryLogs = async () => {
    try {
      const res = await fetch(`${API_URL}/api/scheduled-reports/delivery-logs?limit=50`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setDeliveryLogs(data.logs || []);
      }
    } catch (error) {
      console.error('Failed to fetch logs:', error);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchSchedules(), fetchDeliveryLogs()]);
      setLoading(false);
    };
    if (token) loadData();
  }, [token]);

  const handleCreate = async () => {
    if (!formData.name.trim()) {
      toast.error('Please enter a schedule name');
      return;
    }
    
    const recipients = formData.recipients.split(',').map(e => e.trim()).filter(Boolean);
    if (recipients.length === 0) {
      toast.error('Please enter at least one recipient email');
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/scheduled-reports/schedules`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          ...formData,
          recipients
        })
      });

      if (res.ok) {
        toast.success('Schedule created successfully');
        setShowCreateDialog(false);
        resetForm();
        fetchSchedules();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to create schedule');
      }
    } catch (error) {
      toast.error('Failed to create schedule');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdate = async () => {
    if (!selectedSchedule) return;

    const recipients = formData.recipients.split(',').map(e => e.trim()).filter(Boolean);

    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/scheduled-reports/schedules/${selectedSchedule.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          ...formData,
          recipients
        })
      });

      if (res.ok) {
        toast.success('Schedule updated successfully');
        setShowEditDialog(false);
        fetchSchedules();
      } else {
        toast.error('Failed to update schedule');
      }
    } catch (error) {
      toast.error('Failed to update schedule');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (scheduleId) => {
    if (!confirm('Are you sure you want to delete this schedule?')) return;

    try {
      const res = await fetch(`${API_URL}/api/scheduled-reports/schedules/${scheduleId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success('Schedule deleted');
        fetchSchedules();
      } else {
        toast.error('Failed to delete schedule');
      }
    } catch (error) {
      toast.error('Failed to delete schedule');
    }
  };

  const handleToggle = async (schedule) => {
    try {
      const res = await fetch(`${API_URL}/api/scheduled-reports/schedules/${schedule.id}/toggle`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        fetchSchedules();
      }
    } catch (error) {
      toast.error('Failed to toggle schedule');
    }
  };

  const handleSendNow = async (scheduleId) => {
    try {
      const res = await fetch(`${API_URL}/api/scheduled-reports/schedules/${scheduleId}/send-now`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success('Report sent successfully');
        fetchDeliveryLogs();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to send report');
      }
    } catch (error) {
      toast.error('Failed to send report');
    }
  };

  const handleSendTest = async () => {
    if (!selectedSchedule || !testEmail) return;

    setSendingTest(true);
    try {
      const res = await fetch(
        `${API_URL}/api/scheduled-reports/schedules/${selectedSchedule.id}/test?test_email=${encodeURIComponent(testEmail)}`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (res.ok) {
        toast.success(`Test report sent to ${testEmail}`);
        setTestEmail('');
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to send test email');
      }
    } catch (error) {
      toast.error('Failed to send test email');
    } finally {
      setSendingTest(false);
    }
  };

  const handlePreview = async (schedule) => {
    setSelectedSchedule(schedule);
    try {
      const res = await fetch(`${API_URL}/api/scheduled-reports/preview/${schedule.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        const data = await res.json();
        setPreviewHtml(data.preview_html);
        setShowPreviewDialog(true);
      }
    } catch (error) {
      toast.error('Failed to load preview');
    }
  };

  const openEditDialog = (schedule) => {
    setSelectedSchedule(schedule);
    setFormData({
      name: schedule.name,
      frequency: schedule.frequency,
      report_type: schedule.report_type,
      domains: schedule.domains || ['all'],
      recipients: (schedule.recipients || []).join(', '),
      is_active: schedule.is_active
    });
    setShowEditDialog(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      frequency: 'weekly',
      report_type: 'summary',
      domains: ['all'],
      recipients: '',
      is_active: true
    });
    setSelectedSchedule(null);
    setTestEmail('');
  };

  const toggleDomain = (domain) => {
    if (domain === 'all') {
      setFormData({ ...formData, domains: ['all'] });
    } else {
      let newDomains = formData.domains.filter(d => d !== 'all');
      if (newDomains.includes(domain)) {
        newDomains = newDomains.filter(d => d !== domain);
      } else {
        newDomains.push(domain);
      }
      if (newDomains.length === 0) newDomains = ['all'];
      setFormData({ ...formData, domains: newDomains });
    }
  };

  if (loading) {
    return (
      <Card className={className}>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-[#00b0ab]" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className} data-testid="scheduled-report-manager">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Scheduled Report Delivery
            </CardTitle>
            <CardDescription>
              Configure automated report delivery via email
            </CardDescription>
          </div>
          <Button onClick={() => { resetForm(); setShowCreateDialog(true); }} data-testid="create-schedule-btn">
            <Plus className="h-4 w-4 mr-2" />
            New Schedule
          </Button>
        </div>
      </CardHeader>

      <CardContent>
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="schedules" data-testid="tab-schedules">
              <Calendar className="h-4 w-4 mr-2" />
              Schedules ({schedules.length})
            </TabsTrigger>
            <TabsTrigger value="history" data-testid="tab-history">
              <History className="h-4 w-4 mr-2" />
              Delivery History
            </TabsTrigger>
          </TabsList>

          <TabsContent value="schedules" className="space-y-4 mt-4">
            {schedules.length === 0 ? (
              <div className="text-center py-12">
                <Mail className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                <h3 className="font-medium text-lg">No Schedules Yet</h3>
                <p className="text-muted-foreground mt-2">
                  Create your first scheduled report to start receiving automated updates
                </p>
                <Button className="mt-4" onClick={() => setShowCreateDialog(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Create Schedule
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {schedules.map((schedule) => (
                  <Card key={schedule.id} className={`border ${schedule.is_active ? 'border-emerald-200 bg-emerald-50/50 dark:bg-emerald-900/10' : 'border-slate-200 opacity-60'}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h4 className="font-semibold">{schedule.name}</h4>
                            <Badge variant={schedule.is_active ? 'default' : 'secondary'}>
                              {schedule.is_active ? 'Active' : 'Paused'}
                            </Badge>
                            <Badge variant="outline" className="capitalize">
                              {schedule.frequency}
                            </Badge>
                            <Badge variant="outline">
                              {schedule.report_type === 'full' ? 'PDF' : 'Summary'}
                            </Badge>
                          </div>
                          
                          <div className="flex flex-wrap gap-1 mb-2">
                            {schedule.domains?.map(domain => (
                              <Badge key={domain} variant="secondary" className="text-xs">
                                {DOMAIN_OPTIONS.find(d => d.value === domain)?.label || domain}
                              </Badge>
                            ))}
                          </div>

                          <div className="flex items-center gap-4 text-sm text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Users className="h-3 w-3" />
                              {schedule.recipients?.length || 0} recipients
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              Next: {schedule.next_run}
                            </span>
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          <Switch
                            checked={schedule.is_active}
                            onCheckedChange={() => handleToggle(schedule)}
                          />
                          <Button variant="ghost" size="icon" onClick={() => handlePreview(schedule)} title="Preview">
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => openEditDialog(schedule)} title="Edit">
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleSendNow(schedule.id)} title="Send Now">
                            <Send className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleDelete(schedule.id)} title="Delete" className="text-red-500 hover:text-red-700">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="history" className="mt-4">
            <ScrollArea className="h-[400px]">
              {deliveryLogs.length === 0 ? (
                <div className="text-center py-12">
                  <History className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                  <p className="text-muted-foreground">No delivery history yet</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {deliveryLogs.map((log, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                      <div className="flex items-center gap-3">
                        {log.success ? (
                          <CheckCircle2 className="h-5 w-5 text-emerald-500" />
                        ) : (
                          <XCircle className="h-5 w-5 text-red-500" />
                        )}
                        <div>
                          <p className="font-medium text-sm">{log.schedule_name}</p>
                          <p className="text-xs text-muted-foreground">
                            {log.recipients?.length || 0} recipients • {log.frequency}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-muted-foreground">
                          {new Date(log.delivered_at).toLocaleString()}
                        </p>
                        {!log.success && log.error && (
                          <p className="text-xs text-red-500">{log.error}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </TabsContent>
        </Tabs>
      </CardContent>

      {/* Create/Edit Dialog */}
      <Dialog open={showCreateDialog || showEditDialog} onOpenChange={(open) => {
        if (!open) {
          setShowCreateDialog(false);
          setShowEditDialog(false);
          resetForm();
        }
      }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{showEditDialog ? 'Edit Schedule' : 'Create New Schedule'}</DialogTitle>
            <DialogDescription>
              Configure automated report delivery settings
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Schedule Name</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Weekly Executive Summary"
                  data-testid="schedule-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Frequency</Label>
                <Select value={formData.frequency} onValueChange={(v) => setFormData({ ...formData, frequency: v })}>
                  <SelectTrigger data-testid="frequency-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FREQUENCY_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>
                        <span className="flex items-center gap-2">
                          <span>{opt.icon}</span>
                          <span>{opt.label}</span>
                          <span className="text-xs text-muted-foreground">({opt.description})</span>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Report Type</Label>
              <div className="grid grid-cols-2 gap-3">
                {REPORT_TYPES.map(type => (
                  <div
                    key={type.value}
                    className={`p-3 border rounded-lg cursor-pointer transition-all ${
                      formData.report_type === type.value 
                        ? 'border-indigo-500 bg-[#00b0ab]/10 dark:bg-indigo-900/20' 
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                    onClick={() => setFormData({ ...formData, report_type: type.value })}
                  >
                    <div className="flex items-center gap-2">
                      {type.value === 'full' ? <FileText className="h-4 w-4" /> : <Mail className="h-4 w-4" />}
                      <span className="font-medium text-sm">{type.label}</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">{type.description}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Report Domains</Label>
              <div className="grid grid-cols-3 gap-2">
                {DOMAIN_OPTIONS.map(domain => (
                  <div
                    key={domain.value}
                    className={`p-2 border rounded-lg cursor-pointer transition-all ${
                      formData.domains.includes(domain.value)
                        ? 'border-indigo-500 bg-[#00b0ab]/10 dark:bg-indigo-900/20'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                    onClick={() => toggleDomain(domain.value)}
                  >
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${domain.color}`} />
                      <span className="text-sm font-medium">{domain.label}</span>
                    </div>
                    <p className="text-xs text-muted-foreground">{domain.description}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Recipients (comma-separated emails)</Label>
              <Input
                value={formData.recipients}
                onChange={(e) => setFormData({ ...formData, recipients: e.target.value })}
                placeholder="email1@example.com, email2@example.com"
                data-testid="recipients-input"
              />
            </div>

            {showEditDialog && selectedSchedule && (
              <div className="border-t pt-4 mt-4">
                <Label>Send Test Email</Label>
                <div className="flex gap-2 mt-2">
                  <Input
                    value={testEmail}
                    onChange={(e) => setTestEmail(e.target.value)}
                    placeholder="test@example.com"
                    className="flex-1"
                  />
                  <Button 
                    variant="outline" 
                    onClick={handleSendTest}
                    disabled={!testEmail || sendingTest}
                  >
                    {sendingTest ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                    <span className="ml-2">Send Test</span>
                  </Button>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowCreateDialog(false);
              setShowEditDialog(false);
              resetForm();
            }}>
              Cancel
            </Button>
            <Button 
              onClick={showEditDialog ? handleUpdate : handleCreate}
              disabled={submitting}
              data-testid="save-schedule-btn"
            >
              {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              {showEditDialog ? 'Update Schedule' : 'Create Schedule'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={showPreviewDialog} onOpenChange={setShowPreviewDialog}>
        <DialogContent className="max-w-4xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>Report Preview</DialogTitle>
            <DialogDescription>
              Preview of the email that will be sent to recipients
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="h-[60vh]">
            <div 
              className="border rounded-lg overflow-hidden"
              dangerouslySetInnerHTML={{ __html: previewHtml }}
            />
          </ScrollArea>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreviewDialog(false)}>
              Close
            </Button>
            {selectedSchedule && (
              <Button onClick={() => handleSendNow(selectedSchedule.id)}>
                <Send className="h-4 w-4 mr-2" />
                Send Now
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

export default ScheduledReportManager;
